import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  Tooltip,
  Legend,
} from "chart.js";
import { axiosInstance } from "../../axiosInstance";
import loader from "../../assets/loader.webp";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  Tooltip,
  Legend,
);

const FILTERS = [
  { label: "Today", index: 0 },
  { label: "Yesterday", index: 1 },
  { label: "This Week", index: 2 },
  { label: "Last Week", index: 3 },
  { label: "This Month", index: 4 },
  { label: "Last Month", index: 5 },
  { label: "This Year", index: 6 },
  { label: "Last Year", index: 7 },
];

function TotalSalesChart() {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null); // null = show all

  useEffect(() => {
    const fetchChart = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/superadmin/companies/paychat-chart",
        );
        setRawData(response.data.data);
      } catch (err) {
        console.log("Error fetching PayChat chart", err);
      } finally {
        setLoading(false);
      }
    };
    fetchChart();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40 bg-gray-800 w-full">
        <img src={loader} alt="loader" className="w-10" />
      </div>
    );
  }

  if (!rawData) {
    return (
      <p className="text-gray-400 text-sm bg-gray-800 w-full h-40">
        No chart data available.
      </p>
    );
  }

  const labels =
    activeFilter !== null ? [rawData.labels[activeFilter]] : rawData.labels;

  const data =
    activeFilter !== null
      ? [parseFloat(rawData.datasets[0].data[activeFilter])]
      : rawData.datasets[0].data.map((v) => parseFloat(v));

  const chartData = {
    labels,
    datasets: [
      {
        label: "PayChat Transactions (₦)",
        data,
        backgroundColor: "rgba(59, 130, 246, 0.7)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ` ₦${Number(ctx.raw).toLocaleString("en-NG", {
              minimumFractionDigits: 2,
            })}`,
        },
      },
    },
    scales: {
      y: {
        type: "logarithmic",
        beginAtZero: false,
        ticks: {
          color: "#9ca3af",
          callback: (value) => {
            // Only show clean round numbers to avoid clutter
            const log = Math.log10(value);
            if (log === Math.floor(log) || value === 0) {
              return `₦${Number(value).toLocaleString("en-NG")}`;
            }
            return "";
          },
        },
        grid: { color: "rgba(255,255,255,0.05)" },
      },
      x: {
        ticks: { color: "#9ca3af" },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="bg-gray-800 w-full text-white p-4 rounded-lg flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-2xl font-medium">Total Sales</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveFilter(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              activeFilter === null
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            All
          </button>
          {FILTERS.map((f) => (
            <button
              key={f.index}
              onClick={() =>
                setActiveFilter(activeFilter === f.index ? null : f.index)
              }
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                activeFilter === f.index
                  ? "bg-blue-600 text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: "300px" }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}

export default TotalSalesChart;
